'use client';

import { type JSX, useCallback, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Skeleton } from '@videoblocks/design-system';

import { SelectionIndicator } from './SelectionIndicator';
import { Thumbnail } from './Thumbnail';
import { FILMSTRIP_STRIP_HEIGHT } from './constants';
import { type VideoMetadata, type VideoSelection } from './types';
import { useContainerMeasurement } from './useContainerMeasurement';
import { useThumbnailExtractor } from './useThumbnailExtractor';
import { useVideoThumbnails } from './useVideoThumbnails';
import { timeToAbsolutePixel } from './videoUtils';

const THUMBNAILS_PER_SECOND = 0.5;

interface FilmStripSliderProps {
    videoSrc: string;
    videoMetadata: VideoMetadata | null;
    selection: VideoSelection;
    onSelectionChange: (selection: VideoSelection) => void;
    isPlaying?: boolean;
    onPlayPause?: () => void;
    onSeek?: (time: number) => void;
    className?: string;
}

/**
 * Filmstrip of extracted video frames with a draggable, trimmable selection
 * window used to pick the clip range to edit.
 */
export function FilmStripSlider({
    videoSrc,
    videoMetadata,
    selection,
    onSelectionChange,
    isPlaying = false,
    onPlayPause,
    onSeek,
    className = '',
}: FilmStripSliderProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);

    const { containerWidth } = useContainerMeasurement(containerRef);

    const { thumbnails, times, isReady } = useVideoThumbnails({
        videoMetadata,
        stripHeight: FILMSTRIP_STRIP_HEIGHT,
        containerWidth,
        thumbnailsPerSecond: THUMBNAILS_PER_SECOND,
    });

    const { frameUrls, hasError: extractionFailed } = useThumbnailExtractor({
        videoSrc: videoSrc || null,
        times,
    });

    const handleSelectionMove = useCallback(
        (startTime: number): void => {
            onSelectionChange({
                startTime,
                endTime: startTime + selection.duration,
                duration: selection.duration,
            });
        },
        [selection.duration, onSelectionChange],
    );

    const actualThumbnailHeight = thumbnails.length > 0 ? thumbnails[0].height : FILMSTRIP_STRIP_HEIGHT;

    const isLoaded = Boolean(videoMetadata) && isReady;

    const selectionStartPx =
        isLoaded && videoMetadata
            ? Math.max(0, timeToAbsolutePixel(selection.startTime, videoMetadata.duration, containerWidth))
            : 0;

    const selectionEndPx =
        isLoaded && videoMetadata
            ? Math.min(timeToAbsolutePixel(selection.endTime, videoMetadata.duration, containerWidth), containerWidth)
            : 0;

    return (
        <div ref={containerRef} className={twMerge('relative w-full mb-5', className)}>
            {!isLoaded ? (
                <div
                    className="bg-gray-800 rounded-lg flex items-center justify-center w-full"
                    style={{ height: FILMSTRIP_STRIP_HEIGHT }}
                >
                    {!videoMetadata && <Skeleton className="w-full h-full" data-testid="filmstrip-skeleton" />}
                </div>
            ) : (
                <div className="relative w-full overflow-visible" style={{ height: actualThumbnailHeight }}>
                    <div data-testid="filmstrip-scroll-container" className="overflow-hidden h-full rounded-b-md">
                        <div className="flex select-none relative" style={{ width: containerWidth }}>
                            {thumbnails.map((thumbnail, index) => (
                                <div
                                    key={`${videoSrc}-${thumbnail.timeInSeconds}-${index}`}
                                    className="shrink-0 overflow-hidden"
                                    style={{ width: thumbnail.width }}
                                >
                                    <Thumbnail
                                        thumbnail={thumbnail}
                                        imageSrc={!extractionFailed ? frameUrls[index] : undefined}
                                        videoSrc={extractionFailed ? videoSrc : undefined}
                                    />
                                </div>
                            ))}

                            {/* Unselected left overlay */}
                            <div
                                className="absolute top-0 left-0 bg-black/30 pointer-events-none"
                                style={{ width: selectionStartPx, height: actualThumbnailHeight }}
                            />

                            {/* Unselected right overlay */}
                            <div
                                className="absolute top-0 bg-black/30 pointer-events-none"
                                style={{
                                    left: selectionEndPx,
                                    width: Math.max(0, containerWidth - selectionEndPx),
                                    height: actualThumbnailHeight,
                                }}
                            />
                        </div>

                        <SelectionIndicator
                            selection={selection}
                            videoDuration={videoMetadata?.duration ?? 0}
                            stripWidth={containerWidth}
                            stripHeight={actualThumbnailHeight}
                            onSelectionMove={handleSelectionMove}
                            onSelectionChange={onSelectionChange}
                            onSeek={onSeek}
                            onPlayPause={onPlayPause}
                            isPlaying={isPlaying}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
